import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { Tabs } from '@artosera/ui';
import type { Action } from '@artosera/shared/permissions';
import { usePeut } from '../../auth/usePeut.js';
import pageStyles from '../../styles/page.module.css';
import { AbonnementsTab } from './AbonnementsTab.js';
import { CoutsTab } from './CoutsTab.js';
import { CreancesTab } from './CreancesTab.js';
import { RevenusTab } from './RevenusTab.js';
import { TarifsTab } from './TarifsTab.js';

/**
 * Les onglets et le droit qui ouvre CHACUN en écriture (spec § 3) : sans lui,
 * l'onglet se lit seulement. La grille tarifaire et les coûts sont à la
 * direction ; abonnements, revenus ponctuels et sommes dues, à l'exploitation
 * et à la direction. L'API applique la même matrice.
 */
const ONGLETS = [
  { cle: 'abonnements', action: 'console:manage-subscriptions' },
  { cle: 'tarifs', action: 'console:manage-tariffs' },
  { cle: 'revenus', action: 'console:manage-receivables' },
  { cle: 'creances', action: 'console:manage-receivables' },
  { cle: 'couts', action: 'console:manage-costs' },
] as const satisfies ReadonlyArray<{ cle: string; action: Action }>;

type CleOnglet = (typeof ONGLETS)[number]['cle'];

function estOnglet(valeur: string | null): valeur is CleOnglet {
  return ONGLETS.some((onglet) => onglet.cle === valeur);
}

/**
 * `/finances` : l'espace de saisie financière (ARTO-58). L'onglet courant est
 * dans l'adresse (`?onglet=`), pour que « Gérer » de la vue d'aigle y mène.
 * Rien n'est pré-rempli : un montant vide reste « à saisir ».
 */
export function Finances(): JSX.Element {
  const { t } = useTranslation('console');
  const peut = usePeut();
  const [parametres, setParametres] = useSearchParams();
  const demande = parametres.get('onglet');
  const courant: CleOnglet = estOnglet(demande) ? demande : 'abonnements';
  const enEcriture = (cle: CleOnglet): boolean =>
    peut(ONGLETS.find((onglet) => onglet.cle === cle)!.action);

  const contenu: Record<CleOnglet, JSX.Element> = {
    abonnements: <AbonnementsTab enEcriture={enEcriture('abonnements')} />,
    tarifs: <TarifsTab enEcriture={enEcriture('tarifs')} />,
    revenus: <RevenusTab enEcriture={enEcriture('revenus')} />,
    creances: <CreancesTab enEcriture={enEcriture('creances')} />,
    couts: <CoutsTab enEcriture={enEcriture('couts')} />,
  };

  return (
    <section>
      <div className={pageStyles.entete}>
        <div>
          <h1>{t('finances.titre')}</h1>
          <p>{t('finances.texte')}</p>
        </div>
      </div>

      <Tabs
        value={courant}
        onValueChange={(valeur) =>
          setParametres((precedents) => {
            // Les autres paramètres (`demo`, `role`) restent : l'onglet seul change.
            const suivants = new URLSearchParams(precedents);
            suivants.set('onglet', valeur);
            return suivants;
          })
        }
        tabs={ONGLETS.map(({ cle }) => ({
          value: cle,
          label: t(`finances.onglets.${cle}`),
          content: contenu[cle],
        }))}
      />
    </section>
  );
}
